import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  Box,
  Container,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  styled,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { SEOHead } from '../components/SEOHead';
import { cyber } from '../theme';

const codeBlockTheme = {
  'pre[class*="language-"]': {
    color: '#e6edf3',
    background: '#0f172a',
    borderRadius: '10px',
    border: '1px solid rgba(123, 76, 255, 0.18)',
    padding: '16px 18px',
    overflowX: 'auto' as const,
  },
  'code[class*="language-"]': {
    color: '#e6edf3',
    background: 'transparent',
    fontFamily: cyber.fontMono,
    fontSize: '0.88rem',
    textShadow: 'none',
  },
  comment: { color: '#64748b' },
  prolog: { color: '#64748b' },
  doctype: { color: '#64748b' },
  cdata: { color: '#64748b' },
  punctuation: { color: '#94a3b8' },
  property: { color: '#f472b6' },
  tag: { color: '#f472b6' },
  boolean: { color: '#f97316' },
  number: { color: '#f97316' },
  constant: { color: '#f97316' },
  symbol: { color: '#38bdf8' },
  selector: { color: '#22c55e' },
  'attr-name': { color: '#38bdf8' },
  string: { color: '#86efac' },
  char: { color: '#86efac' },
  builtin: { color: '#38bdf8' },
  inserted: { color: '#86efac' },
  operator: { color: '#cbd5e1' },
  entity: { color: '#cbd5e1' },
  url: { color: '#cbd5e1' },
  atrule: { color: '#c084fc' },
  keyword: { color: '#c084fc' },
  function: { color: '#fde047' },
  'class-name': { color: '#fde047' },
  regex: { color: '#fb7185' },
  important: { color: '#fb7185' },
  variable: { color: '#f8fafc' },
  bold: { fontWeight: 700 },
  italic: { fontStyle: 'italic' as const },
};

// --- Types ---
interface ReportMeta {
  slug: string;
  title: string;
  date: string;
  severity: string;
  classification: string;
  description: string;
  tags: string[];
  sources_count: number;
  author: string;
}

// --- Severity colors ---
const severityColors: Record<string, { bg: string; color: string; border: string }> = {
  CRITICAL: { bg: 'rgba(244, 67, 54, 0.15)', color: '#ff5252', border: 'rgba(244, 67, 54, 0.4)' },
  HIGH: { bg: 'rgba(255, 152, 0, 0.15)', color: '#ffab40', border: 'rgba(255, 152, 0, 0.4)' },
  MEDIUM: { bg: 'rgba(255, 235, 59, 0.15)', color: '#ffee58', border: 'rgba(255, 235, 59, 0.4)' },
  LOW: { bg: 'rgba(76, 175, 80, 0.15)', color: '#69f0ae', border: 'rgba(76, 175, 80, 0.4)' },
  INFORMATIONAL: { bg: 'rgba(33, 150, 243, 0.15)', color: '#448aff', border: 'rgba(33, 150, 243, 0.4)' },
};

// --- Styled ---
const SeverityBadge = styled(Box)<{ severity: string }>(({ severity }) => {
  const colors = severityColors[severity] || severityColors.INFORMATIONAL;
  return {
    display: 'inline-block',
    padding: '3px 12px',
    borderRadius: '14px',
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    background: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
  };
});

const MarkdownContainer = styled(Box)(({ theme }) => ({
  '& h1': {
    fontSize: '1.8rem',
    color: '#fff',
    margin: '32px 0 16px',
    paddingBottom: '8px',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    '&:first-of-type': { marginTop: 0 },
  },
  '& h2': {
    fontSize: '1.4rem',
    color: '#e8e8ff',
    margin: '28px 0 12px',
    paddingBottom: '6px',
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '& h3': {
    fontSize: '1.15rem',
    color: '#d0d0ff',
    margin: '20px 0 10px',
  },
  '& h4': {
    fontSize: '1.05rem',
    color: '#c0c0e0',
    margin: '16px 0 8px',
  },
  '& p': {
    marginBottom: '14px',
    color: '#d0d0d0',
    lineHeight: 1.7,
  },
  '& strong': {
    color: '#fff',
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  '& ul, & ol': {
    margin: '12px 0',
    paddingLeft: '24px',
  },
  '& li': {
    marginBottom: '6px',
    color: '#d0d0d0',
  },
  '& hr': {
    border: 'none',
    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    margin: '28px 0',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: 'rgba(0, 0, 0, 0.2)',
    padding: '12px 20px',
    margin: '16px 0',
    borderRadius: '0 8px 8px 0',
  },
  // Tables
  '& table': {
    width: '100%',
    borderCollapse: 'collapse' as const,
    margin: '16px 0',
    fontSize: '0.9rem',
    display: 'block',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch',
  },
  '& thead': {
    background: alpha(theme.palette.primary.main, 0.15),
  },
  '& th': {
    padding: '10px 14px',
    textAlign: 'left' as const,
    color: '#c0c0ff',
    fontWeight: 600,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    whiteSpace: 'nowrap' as const,
  },
  '& td': {
    padding: '8px 14px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    color: '#c8c8c8',
  },
  '& tbody tr:hover': {
    background: alpha(theme.palette.primary.main, 0.05),
  },
  // Inline code
  '& code:not(pre code)': {
    background: 'rgba(0, 0, 0, 0.3)',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '0.88em',
    color: '#ff9c9c',
    fontFamily: cyber.fontMono,
  },
  // Code blocks handled by SyntaxHighlighter
  '& pre': {
    margin: '16px 0',
    borderRadius: '8px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

// --- Component ---
const AnalyticsReportPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [meta, setMeta] = useState<ReportMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    const fetchData = async () => {
      try {
        // Fetch index to get metadata
        const indexRes = await fetch('/analytics/index.json');
        if (!indexRes.ok) throw new Error('Failed to load report index');
        const index: ReportMeta[] = await indexRes.json();
        const reportMeta = index.find((r) => r.slug === slug);
        if (reportMeta) setMeta(reportMeta);

        // Fetch markdown content
        const mdRes = await fetch(`/analytics/reports/${slug}.md`);
        if (!mdRes.ok) throw new Error('Report not found');
        const mdContent = await mdRes.text();
        setMarkdown(mdContent);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2 }}>
          <Link component={RouterLink} to="/analytics" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowBackIcon fontSize="small" /> Back to Agentic Reports
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <>
      {meta && (
        <SEOHead
          title={meta.title}
          description={meta.description}
          canonical={`/analytics/${meta.slug}`}
          type="article"
          publishedDate={meta.date}
          tags={meta.tags}
        />
      )}

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/analytics" color="inherit" underline="hover" sx={{ fontSize: '0.9rem' }}>
            Agentic Reports
          </Link>
          <Typography color="text.primary" sx={{ fontSize: '0.9rem' }}>
            Report
          </Typography>
        </Breadcrumbs>

        {/* Back link */}
        <Link
          component={RouterLink}
          to="/analytics"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 3, fontSize: '0.9rem' }}
        >
          <ArrowBackIcon fontSize="small" /> All Agentic Reports
        </Link>

        {/* Meta header */}
        {meta && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <SeverityBadge severity={meta.severity}>{meta.severity}</SeverityBadge>
              <Chip
                label={meta.classification}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: cyber.fontMono, fontSize: '0.8rem' }}
              >
                {formatDate(meta.date)}
              </Typography>
              {meta.sources_count > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: alpha(cyber.accent.cyan, 0.9), fontFamily: cyber.fontMono, fontSize: '0.8rem' }}
                >
                  {meta.sources_count} sources
                </Typography>
              )}
            </Box>

            {/* Tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {meta.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    height: 22,
                    bgcolor: alpha(cyber.primary, 0.15),
                    color: cyber.primaryLight,
                    border: '1px solid',
                    borderColor: alpha(cyber.primary, 0.4),
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Report content */}
        <Box
          sx={{
            bgcolor: alpha('#ffffff', 0.03),
            border: 1,
            borderColor: alpha(cyber.primary, 0.15),
            borderRadius: 3,
            p: { xs: 2, md: 5 },
          }}
        >
          <MarkdownContainer>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const codeString = String(children).replace(/\n$/, '');
                  if (match) {
                    return (
                      <SyntaxHighlighter
                        style={codeBlockTheme}
                        language={match[1]}
                        PreTag="div"
                      >
                        {codeString}
                      </SyntaxHighlighter>
                    );
                  }
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {markdown || ''}
            </ReactMarkdown>
          </MarkdownContainer>
        </Box>

        {/* Bottom navigation */}
        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Link
            component={RouterLink}
            to="/analytics"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.9rem' }}
          >
            <ArrowBackIcon fontSize="small" /> Back to All Reports
          </Link>
        </Box>
      </Container>
    </>
  );
};

export default AnalyticsReportPage;
